<template>
  <div class="bg-light card">
    <div class="mx-3 mt-2">
      <label for="location" class="font-weight-bold">Location</label>
      <span class="custom-asterisk-added">&#42;</span>
      <i
        v-if="name && lngLat.lat && lngLat.lng"
        class="fas fa-map-marker-alt text-success"
      ></i>
      <i v-else class="fas fa-map-marker-alt text-danger"></i>
    </div>
    <div class="mx-3 mt-1">
      <div class="row">
        <b-form-group
          id="name-input-group"
          class="col-12 col-md-6"
          label="Name"
          label-for="name"
        >
          <b-form-input
            id="name"
            ref="name"
            v-model="name"
            name="name"
            placeholder="Please name this location..."
            v-validate="{
              required: true,
              min: 5,
              max: 100,
            }"
            data-vv-delay="500"
            :class="{
              'is-invalid': errors.has(`name`),
            }"
            aria-describedby="name-live-feedback"
          ></b-form-input>
          <b-form-invalid-feedback
            id="name-live-feedback"
            v-if="errors.has('name')"
          >
            {{ errors.first("name") }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          id="latitude-input-group"
          class="col-6 col-md-3"
          label="Latitude"
          label-for="latitude"
        >
          <b-form-input
            id="latitude"
            v-model="lngLat.lat"
            type="number"
            :number="true"
            name="latitude"
            placeholder="±90.0000000"
            :step="0.0005"
            v-validate="{
              required: true,
              decimal: 7,
              min_value: -90,
              max_value: 90,
            }"
            data-vv-delay="2000"
            @update="updateTexbox"
            :class="{
              'is-invalid': errors.has(`latitude`),
            }"
            aria-describedby="latitude-live-feedback"
          ></b-form-input>
          <b-form-invalid-feedback
            id="latitude-live-feedback"
            v-if="errors.has('latitude')"
          >
            {{ errors.first("latitude") }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          id="longitude-input-group"
          class="col-6 col-md-3"
          label="Longitude"
          label-for="longitude"
        >
          <b-form-input
            id="longitude"
            v-model="lngLat.lng"
            type="number"
            :number="true"
            name="longitude"
            placeholder="±180.0000000"
            :step="0.0005"
            v-validate="{
              required: true,
              decimal: 7,
              min_value: -180,
              max_value: 180,
            }"
            data-vv-delay="2000"
            @update="updateTexbox"
            :class="{
              'is-invalid': errors.has(`longitude`),
            }"
            aria-describedby="longitude-live-feedback"
          ></b-form-input>
          <b-form-invalid-feedback
            id="longitude-live-feedback"
            v-if="errors.has('longitude')"
          >
            {{ errors.first("longitude") }}
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
    </div>
    <div class="position-relative border-top border-bottom">
      <LMap
        class="location-map"
        :zoom="map.zoom"
        :center="map.center"
        @update:center="centerUpdate"
        @click="mapClick"
        ref="leaflet"
        :options="map.options"
        @ready="addBasemapLayer(BASEMAP_ACTIVE)"
      >
        <LLayerGroup>
          <LMarker
            :lat-lng.sync="lngLat"
            v-if="lngLat.lat && lngLat.lng"
            :draggable="true"
            @moveend="moveend"
          >
            <MarkerPin type="primary"><i class="fa fa-plus"></i></MarkerPin>
            <LTooltip
              ><div>Observation Location</div>
              <div>Drag to move</div></LTooltip
            >
          </LMarker>
          <LMarker :lat-lng="home" v-if="home && home.lat && home.lng">
            <MarkerPin><i class="fa fa-home"></i></MarkerPin>
            <LTooltip content="Project Home" />
          </LMarker>
          <LRectangle
            v-if="searchArea.area && searchArea.area.bounds"
            :bounds="searchArea.area.bounds"
            :l-style="searchArea.style"
          >
          </LRectangle>
          <LCircle
            v-else-if="
              searchArea.area &&
              searchArea.area.lngLat &&
              searchArea.area.accuracy
            "
            :lat-lng="searchArea.area.lngLat"
            :radius="searchArea.area.accuracy"
            :l-style="searchArea.style"
          >
          </LCircle>
        </LLayerGroup>
        <LControlZoom position="bottomright"></LControlZoom>
      </LMap>
      <div class="map-layers bg-white border rounded position-absolute">
        <BaseMapPicker class="p-1" />
      </div>
      <div
        class="position-absolute map-help text-center"
        v-if="!isMapClicked"
        :class="{
          border: errors.has('latitude') || errors.has('longitude'),
          'border-danger': errors.has('latitude') || errors.has('longitude'),
        }"
      >
        <div>Click / tap to choose location</div>
        <div>Drag marker to move it</div>
      </div>
      <GpsGeocoderInput
        v-on:input="setMapView"
        ref="GpsGeocoderInput"
        class="position-absolute map-search"
      />
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";

import {
  LMap,
  LTileLayer,
  LLayerGroup,
  LMarker,
  LPopup,
  LTooltip,
  LRectangle,
  LCircle,
  LControlZoom,
} from "vue2-leaflet";

import MarkerCrosshairs from "../../common/map/MarkerCrosshairs.vue";
import MarkerPin from "../../common/map/MarkerPin.vue";
import GpsGeocoderInput from "../../common/map/gpsGeocoderInput.vue";
import BaseMapPicker from "../../common/map/BaseMapPicker.vue";

export default {
  name: "DatasheetLocationNew",
  components: {
    LMap,
    LTileLayer,
    LLayerGroup,
    LMarker,
    LPopup,
    LTooltip,
    LRectangle,
    LCircle,
    LControlZoom,
    MarkerCrosshairs,
    MarkerPin,
    GpsGeocoderInput,
    BaseMapPicker,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    home: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      name: null,
      lngLat: { lng: null, lat: null },

      map: {
        zoom: 6,
        center: { lng: -90, lat: 40 },
        options: {
          zoomControl: false,
        },
      },
      searchArea: {
        area: null,
        style: {
          color: "#000000",
          weight: 10,
          opacity: 0.8,
          fillOpacity: 0,
        },
        tooltipStyle: {
          permanent: true,
          direction: "top",
          offset: [0, 100],
        },
      },
      isMapClicked: false,
    };
  },
  mounted() {
    if (this.home && this.home.lat && this.home.lng) {
      this.map.center = this.home;
    }
  },
  computed: {
    ...mapGetters({
      BASEMAP_ACTIVE: "map/BASEMAP_ACTIVE",
    }),
  },
  methods: {
    // Component methods
    ...mapActions({}),
    centerUpdate(center) {
      this.map.center = center;
    },
    mapClick(e) {
      this.isMapClicked = true;
      this.lngLat = {
        lng: e.latlng.lng.toFixed(7),
        lat: e.latlng.lat.toFixed(7),
      };
      // Close the GpsGeocoderInput list if open
      this.$refs.GpsGeocoderInput &&
        this.$refs.GpsGeocoderInput.$emit("close-list");

      this.$nextTick(() => {
        if (this.name) return;
        let focusRef = this.$refs.name;
        focusRef.$refs.input.focus();
      });
    },
    moveend(e) {
      this.lngLat = {
        lng: e.sourceTarget._latlng.lng.toFixed(7),
        lat: e.sourceTarget._latlng.lat.toFixed(7),
      };
    },
    updateTexbox() {
      this.$nextTick(() => {
        if (this.lngLat && this.lngLat.lng && this.lngLat.lat) {
          this.$refs.leaflet.mapObject.flyTo(this.lngLat);
        }
      });
    },
    updateInput() {
      this.$emit("input", {
        name: this.name,
        lngLat: this.lngLat,
        "@id": null,
      });
    },
    setMapView(searchArea) {
      this.searchArea.area = searchArea;
      if (searchArea && searchArea.bounds) {
        this.$refs.leaflet.mapObject.flyToBounds(searchArea.bounds);
      } else if (searchArea && searchArea.lngLat) {
        this.$refs.leaflet.mapObject.flyTo(searchArea.lngLat, searchArea.zoom);
      }
    },
    validateLocation() {
      return this.$validator.validateAll().then((result) => {
        if (this.errors.has("latitude") || this.errors.has("longitude")) {
          this.isMapClicked = false;
        }
        return result;
      });
    },
    addBasemapLayer(newLayer, oldLayer) {
      const map = this.$refs.leaflet && this.$refs.leaflet.mapObject;
      if (!map || !newLayer) return;
      if (oldLayer) {
        try {
          map.removeLayer(oldLayer);
        } catch (e) {}
      }
      newLayer.addTo(map);
      map.invalidateSize();
    },
  },
  watch: {
    BASEMAP_ACTIVE(newLayer, oldLayer) {
      this.addBasemapLayer(newLayer, oldLayer);
    },
    lngLat: {
      handler() {
        this.updateInput();
      },
      deep: true,
    },
    name() {
      this.updateInput();
    },
  },
};
</script>
<style scoped>
.location-map {
  width: 100%;
  height: 30vh;
  min-height: 300px;
}
.map-help {
  z-index: 999;
  top: 25%;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 10px;
  border-radius: 10px;
}
.map-search {
  top: 10px;
  left: 10px;
  right: 10px;
  z-index: 500;

  @media (min-width: 768px) {
    width: 50%;
  }

  @media (min-width: 992px) {
    width: 33%;
  }
}
.map-layers {
  z-index: 999;
  bottom: 10px;
  left: 10px;

  @media (min-width: 768px) {
    position: absolute;
    bottom: 10px;
    left: 10px;
    z-index: 999;
  }
}
</style>
